import { useEffect, useState } from 'react'

import { styled } from '../core'
import { Box, Button, Card, DismissButton, H4, Icon, IconButton } from '../index'
import { tutorialColors } from './consts'

const TutorialHeader = styled(Box)`
  display: flex;
  flex-direction: row;
  flex: 0;
  flex-grow: 0;
  -webkit-flex-grow: 0;
  align-content: center;
  align-items: center;
  justify-content: space-between;
  min-height: 48px;
  --vui-dismiss-fg: var(--vui-action-contrast-solid);
`
import type { TutorialCardProps, TutorialDataStep, TutorialStepWrapper } from './tutorial.types'

import { TutorialStep } from './tutorialStep'

export function TutorialCard({
  actionLabel = 'Action',
  className,
  title,
  readMoreLink,
  steps: propsSteps = [],
  showSkipButton,
  onAction,
  onDismiss,
  onFinish,
  includeFinalPage,
  useContentTitle,
}: TutorialCardProps) {
  const [steps, setSteps] = useState<TutorialStepWrapper[]>([])
  const [activeStep, setActiveStep] = useState<number>(0)

  useEffect(() => {
    if (title && propsSteps.length) {
      const s = propsSteps.map((step, key) => ({ active: false, step, key }))

      const finalStep: TutorialDataStep = {
        title: `Completed!`,
        content: `${readMoreLink ? 'To learn more, please visit:' : 'Thank you!'}`,
      }

      if (readMoreLink) finalStep.readMoreLink = readMoreLink

      includeFinalPage ? setSteps([...s, { step: finalStep, key: propsSteps.length }]) : setSteps([...s])
    }
  }, [title, propsSteps])

  const isStartStep = activeStep === 0
  const isFinalStep = includeFinalPage ? activeStep === propsSteps.length : activeStep === propsSteps.length - 1

  return (
    <Card
      bg={tutorialColors.bg}
      className={className}
      color={tutorialColors.color}
      column
      justifyContent="space-between"
      minH={400}
      px={3}
      py={2}
      w={580}
    >
      <TutorialHeader className={className + '__header'}>
        <H4 color={tutorialColors.color}>{useContentTitle ? steps[activeStep]?.step?.title : title}</H4>
        {!isFinalStep && <DismissButton aria-label="close" icon="uiTimes" onClick={onDismiss} scale={2} />}
      </TutorialHeader>
      <Box flex={5} row>
        {steps?.map?.(s => (
          <TutorialStep
            active={s.key === activeStep}
            content={s.step.content}
            key={s.step.title}
            readMoreLink={s.step.readMoreLink}
            title={s.step.title}
            useContentTitle={useContentTitle}
          />
        ))}
      </Box>
      <Box alignItems="center" justifyContent="space-between" mt={0}>
        <Box alignItems="center" justifyContent="space-between" w="-webkit-fill-available">
          <IconButton
            aria-label="previous"
            color={tutorialColors.buttonColor}
            disabled={isStartStep}
            icon="uiArrowLeft"
            onClick={() => setActiveStep(activeStep - 1)}
            scale={2}
            transition="none"
            variant="tertiary"
          />
          <Box className="vuiTutorialStepDots">
            {steps?.map?.(s => (
              <Icon
                color={s.key === activeStep ? tutorialColors.color : 'darkBlue.30'}
                cursor="pointer"
                hoverColor={tutorialColors.buttonColor}
                key={s.key}
                name="uiDotFull"
                onClick={() => setActiveStep(s.key)}
              />
            ))}
          </Box>
          <IconButton
            aria-label="next"
            color={tutorialColors.buttonColor}
            disabled={isFinalStep}
            icon="uiArrowRight"
            onClick={() => setActiveStep(activeStep + 1)}
            scale={2}
            transition="none"
            variant="tertiary"
          />
        </Box>
        {(showSkipButton || isFinalStep || !!onAction) && (
          <Box alignItems="center" justifyContent="space-between">
            {showSkipButton && !isFinalStep && (
              <Button mx={1} onClick={onDismiss} variant="secondary" intent="contrast">
                Skip
              </Button>
            )}
            {isFinalStep && (
              <Button mx={1} onClick={onFinish} variant="primary" intent="contrast" minW="82px">
                I got it
              </Button>
            )}
            {!!onAction && (
              <Button
                ml={1}
                mr={showSkipButton || isFinalStep ? 12 : 2}
                onClick={onAction}
                variant="primary"
                intent="contrast"
              >
                {actionLabel}
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Card>
  )
}

TutorialCard.displayName = 'TutorialCard'
